/**
 * Palisade core engine — public entry point.
 * Phase 1: classic Quoridor. Phase 2: character abilities + map gimmicks.
 *
 * Contract: docs/CORE_INTERFACE.md
 * - Pure JS (ESM), no three.js / DOM / Node APIs.
 * - State is a plain JSON-serializable object.
 * - applyMove returns a NEW state and never mutates its arguments.
 *
 * Phase 2 architecture (spec §8): the engine knows only generic hooks —
 * it never branches on character names.
 *   - characters.js : data table (uses / passives) + context builders
 *   - abilities.js  : pluggable effect registry (generate / apply / flags)
 *   - gimmicks.js   : deterministic tile modifiers (ice slide, swamp)
 * Classic mode (characters ['none','none'], gimmicks []) is unchanged.
 *
 * Only this module is public; the other src/core modules are internal.
 */

import {
  buildWallSet,
  wallInBounds,
  wallConflicts,
  wallKey,
} from './walls.js';
import { pawnMovesFor } from './movement.js';
import { bfsDistanceToRow } from './path.js';
import { ABILITIES } from './abilities.js';
import {
  characterDef,
  ignoresGimmicks,
  moveContextFor,
  pathContextFor,
} from './characters.js';
import {
  isSwampAt,
  buildGimmickMap,
  resolveSlide,
  cellKey,
} from './gimmicks.js';

const DEFAULT_CONFIG = {
  boardSize: 9,
  wallsPerPlayer: 10,
  characters: ['none', 'none'],
  gimmicks: [], // [{ type: 'ice'|'swamp', col, row }, ...] — deterministic
  seed: 0, // future event mode (unused)
};

/** Is this player under the hexer's curse (whole next turn skipped)? */
function isHexed(player) {
  return !!(player.effects && player.effects.hexed);
}

/** Deep clone for plain JSON-serializable values. */
function clone(value) {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

/**
 * Create a fresh game state. `partialConfig` is merged over the defaults.
 * Character passives (extra walls) and active abilities (public usesLeft)
 * are initialized from the data table in characters.js.
 *
 * Optional `config.position` seeds a mid-game board (single-mode stages,
 * scenario setups) as plain JSON — see seedPosition() for the accepted
 * shape and the validation it enforces. When omitted the returned state is
 * byte-for-byte identical to the classic fresh-game state.
 */
export function createGame(partialConfig = {}) {
  const config = { ...clone(DEFAULT_CONFIG), ...clone(partialConfig) };
  const size = config.boardSize;
  const startCol = Math.floor(size / 2);

  // `position` is a one-shot seed, not persistent game config — pull it out
  // so state.config stays the classic { boardSize, wallsPerPlayer, characters,
  // gimmicks, seed } shape (the AI / renderer read config, never a stale seed).
  const position = config.position;
  delete config.position;

  const makePlayer = (index) => {
    const characterId = (config.characters ?? [])[index] ?? 'none';
    const def = characterDef(characterId);
    return {
      pawn: { col: startCol, row: index === 0 ? 0 : size - 1 },
      goalRow: index === 0 ? size - 1 : 0,
      wallsLeft: config.wallsPerPlayer + (def.passive.extraWalls ?? 0),
      character: characterId,
      // Remaining ability uses are PUBLIC information (spec §7-8).
      ability: def.active
        ? { id: def.active.ability, usesLeft: def.active.uses }
        : null,
      effects: {}, // tile-gimmick effects, e.g. { swamped: true }
    };
  };

  const state = {
    config,
    players: [makePlayer(0), makePlayer(1)],
    walls: [],
    current: 0,
    turn: 1,
    winner: null,
    history: [],
  };

  if (position != null) seedPosition(state, position);
  return state;
}

/**
 * Apply a preset mid-game `position` onto a freshly built `state`, in place.
 * Shape (every field optional):
 *   pawns:           [{ col, row }, ...]  per player (null entry = keep default)
 *   walls:           [{ col, row, orientation, owner? }, ...]
 *   wallsRemaining:  [n, ...]             per-player wallsLeft override
 *   currentPlayer:   0 | 1                whose turn it is
 *
 * Validates by the SAME rules the engine enforces during play (reusing
 * walls.js / path.js helpers, no duplicated geometry): pawns in bounds and
 * non-overlapping; walls in bounds, well-oriented, and non-crossing; and a
 * path to goal must remain for BOTH players. Throws a descriptive Error on
 * any violation so malformed stage data fails loudly at build time.
 */
function seedPosition(state, position) {
  const size = state.config.boardSize;
  const n = state.players.length;

  if (typeof position !== 'object') {
    throw new Error('position must be an object');
  }

  // --- pawns ---
  if (position.pawns != null) {
    if (!Array.isArray(position.pawns) || position.pawns.length !== n) {
      throw new Error(`position.pawns must be an array of ${n} cells`);
    }
    position.pawns.forEach((cell, i) => {
      if (cell == null) return; // keep this player's default start cell
      if (
        !Number.isInteger(cell.col) ||
        !Number.isInteger(cell.row) ||
        cell.col < 0 ||
        cell.row < 0 ||
        cell.col >= size ||
        cell.row >= size
      ) {
        throw new Error(
          `position.pawns[${i}] out of bounds: ${JSON.stringify(cell)}`
        );
      }
      state.players[i].pawn = { col: cell.col, row: cell.row };
    });
    const seen = new Set();
    for (let i = 0; i < n; i++) {
      const key = cellKey(state.players[i].pawn.col, state.players[i].pawn.row);
      if (seen.has(key)) {
        throw new Error('position: pawns overlap on the same cell');
      }
      seen.add(key);
    }
  }

  // --- walls (validated incrementally, same rules as a wall move) ---
  if (position.walls != null) {
    if (!Array.isArray(position.walls)) {
      throw new Error('position.walls must be an array');
    }
    const wallSet = new Set();
    for (const w of position.walls) {
      if (w == null || (w.orientation !== 'h' && w.orientation !== 'v')) {
        throw new Error(`position.walls: bad wall ${JSON.stringify(w)}`);
      }
      if (!wallInBounds(size, w.col, w.row)) {
        throw new Error(`position.walls: out of bounds ${JSON.stringify(w)}`);
      }
      if (wallConflicts(wallSet, w.col, w.row, w.orientation)) {
        throw new Error(
          `position.walls: overlaps/crosses an existing wall ${JSON.stringify(w)}`
        );
      }
      wallSet.add(wallKey(w.col, w.row, w.orientation));
      state.walls.push({
        col: w.col,
        row: w.row,
        orientation: w.orientation,
        owner: w.owner === 1 ? 1 : 0,
      });
    }
  }

  // --- per-player wall counts ---
  if (position.wallsRemaining != null) {
    if (
      !Array.isArray(position.wallsRemaining) ||
      position.wallsRemaining.length !== n
    ) {
      throw new Error(`position.wallsRemaining must be an array of ${n} numbers`);
    }
    position.wallsRemaining.forEach((count, i) => {
      if (count == null) return;
      if (!Number.isInteger(count) || count < 0) {
        throw new Error(`position.wallsRemaining[${i}] must be a non-negative integer`);
      }
      state.players[i].wallsLeft = count;
    });
  }

  // --- whose turn ---
  if (position.currentPlayer != null) {
    if (
      !Number.isInteger(position.currentPlayer) ||
      position.currentPlayer < 0 ||
      position.currentPlayer >= n
    ) {
      throw new Error(`position.currentPlayer out of range: ${position.currentPlayer}`);
    }
    state.current = position.currentPlayer;
  }

  // --- reachability: every player must still be able to reach their goal ---
  for (let i = 0; i < n; i++) {
    if (shortestPathLength(state, i) < 0) {
      throw new Error(`position: player ${i} has no path to their goal row`);
    }
  }
}

/**
 * Movable destination cells for a player's pawn (UI highlight helper).
 * Cells are final resting cells (ice slides already applied); empty while
 * the player is swamped.
 */
export function pawnMoves(state, playerIndex) {
  return pawnMovesFor(state, playerIndex);
}

/** BFS: does the player still have a route to their goal row? */
export function pathExists(state, playerIndex) {
  return shortestPathLength(state, playerIndex) >= 0;
}

/**
 * Shortest number of moves from the player's pawn to their goal row
 * (walls only; pawns do not block). Character-aware (§8-8): counts the
 * ninja's remaining wall jumps and folds ice slides into the graph.
 * Returns -1 if no path exists — never the case in a legally reached state.
 */
export function shortestPathLength(state, playerIndex) {
  const player = state.players[playerIndex];
  return bfsDistanceToRow(
    state.config.boardSize,
    buildWallSet(state.walls),
    player.pawn,
    player.goalRow,
    pathContextFor(state, playerIndex)
  );
}

/**
 * Would placing this wall leave every player a path to their goal?
 * (wallSet already contains existing walls; candidate passed separately.)
 * Each player is checked with their OWN path context — e.g. a wall that
 * fully encloses a ninja is legal while the ninja still has a jump left,
 * and becomes illegal once the jump is spent (spec §8-8).
 */
function wallKeepsPaths(state, wallSet, col, row, orientation) {
  const key = wallKey(col, row, orientation);
  wallSet.add(key);
  let ok = true;
  for (let i = 0; i < state.players.length; i++) {
    const player = state.players[i];
    if (
      bfsDistanceToRow(
        state.config.boardSize,
        wallSet,
        player.pawn,
        player.goalRow,
        pathContextFor(state, i)
      ) < 0
    ) {
      ok = false;
      break;
    }
  }
  wallSet.delete(key);
  return ok;
}

function isLegalWallPlacement(state, wallSet, col, row, orientation) {
  const size = state.config.boardSize;
  if (orientation !== 'h' && orientation !== 'v') return false;
  if (!wallInBounds(size, col, row)) return false;
  if (state.players[state.current].wallsLeft <= 0) return false;
  if (wallConflicts(wallSet, col, row, orientation)) return false;
  return wallKeepsPaths(state, wallSet, col, row, orientation);
}

/**
 * Ability context — the candidate-generation context (boardSize, wallSet,
 * occupied, gimmickMap for the actor) plus closures that let an ability
 * reason about ANOTHER player. shove/swap relocate the opponent, so they
 * must resolve ice slides and path guards from the OPPONENT's point of view
 * (their gimmick immunity, their remaining jumps) — which the base actor
 * context cannot express. Keeping this in the engine (rather than importing
 * character helpers into abilities.js) avoids a characters<->abilities import
 * cycle and keeps all character-aware policy on the engine side.
 *
 * The same builder feeds both generate() (over the pre-move `state`) and
 * apply() (over the cloned, pre-pawn-move `next`); since pawn positions and
 * walls are identical at those two moments, slideFor() is guaranteed to
 * agree, so apply() can recompute a shove destination deterministically
 * instead of trusting an (unvalidated) extra field on the move.
 */
function abilityContext(state, playerIndex) {
  const base = moveContextFor(state, playerIndex);
  const { boardSize, wallSet } = base;
  const gimmicks = state.config.gimmicks;
  const gimmickMapFor = (i) =>
    gimmicks && gimmicks.length > 0 && !ignoresGimmicks(state, i)
      ? buildGimmickMap(gimmicks)
      : null;
  const slideFor = (i, cell, dc, dr) => {
    const occ = new Set();
    for (let j = 0; j < state.players.length; j++) {
      if (j === i) continue;
      const p = state.players[j].pawn;
      occ.add(cellKey(p.col, p.row));
    }
    return resolveSlide(boardSize, wallSet, gimmickMapFor(i), occ, cell, dc, dr);
  };
  const reachesGoal = (i, cell) =>
    bfsDistanceToRow(
      boardSize,
      wallSet,
      cell,
      state.players[i].goalRow,
      pathContextFor(state, i)
    ) >= 0;
  return { ...base, gimmickMapFor, slideFor, reachesGoal };
}

/**
 * Legal ability moves for the current player (generic hook — the engine
 * only consults the player's public ability charge and the registry).
 * Movement abilities are blocked while swamped.
 */
function abilityMovesFor(state) {
  const player = state.players[state.current];
  if (!player.ability || player.ability.usesLeft <= 0) return [];
  const def = ABILITIES[player.ability.id];
  if (!def) return [];
  if (def.movement && player.effects && player.effects.swamped) return [];
  return def.generate(state, state.current, abilityContext(state, state.current));
}

/** Structural match between a generated candidate and a user-given move. */
function sameAbilityMove(candidate, move) {
  if (move.ability !== candidate.ability) return false;
  if (candidate.to) {
    return (
      !!move.to &&
      move.to.col === candidate.to.col &&
      move.to.row === candidate.to.row
    );
  }
  if (candidate.at) {
    return (
      !!move.at &&
      move.at.col === candidate.at.col &&
      move.at.row === candidate.at.row &&
      move.orientation === candidate.orientation
    );
  }
  return false;
}

/** All moves except the stalemate-escape 'pass'. Assumes winner === null. */
function nonPassMoves(state) {
  // Hexed: the whole turn is skipped — no pawn move, wall, or ability. The
  // only legal move is `pass`, which clears the curse in applyMove (symmetric
  // to the swamped movement-lock, but total). Kept here so BOTH legalMoves and
  // isLegal('pass') agree without special-casing every branch below.
  if (isHexed(state.players[state.current])) return [];

  const moves = [];
  for (const to of pawnMovesFor(state, state.current)) {
    moves.push({ type: 'move', to });
  }

  if (state.players[state.current].wallsLeft > 0) {
    const size = state.config.boardSize;
    const wallSet = buildWallSet(state.walls);
    for (const orientation of ['h', 'v']) {
      for (let col = 0; col <= size - 2; col++) {
        for (let row = 0; row <= size - 2; row++) {
          if (isLegalWallPlacement(state, wallSet, col, row, orientation)) {
            moves.push({ type: 'wall', at: { col, row }, orientation });
          }
        }
      }
    }
  }

  moves.push(...abilityMovesFor(state));
  return moves;
}

/**
 * All legal moves for the current player. Empty once a winner is set.
 * If the player has no move at all (e.g. swamped with no walls and no
 * usable ability), a single { type: 'pass' } is offered so the game can
 * never deadlock — unreachable in classic play.
 */
export function legalMoves(state) {
  if (state.winner !== null) return [];
  const moves = nonPassMoves(state);
  if (moves.length === 0) moves.push({ type: 'pass' });
  return moves;
}

/** Is this move legal for the current player in this state? */
export function isLegal(state, move) {
  if (state.winner !== null) return false;
  if (!move || typeof move !== 'object') return false;

  // Hexed: pass is the only legal move (see nonPassMoves). Guard up front so no
  // move/wall/ability branch below can independently accept anything.
  if (isHexed(state.players[state.current])) return move.type === 'pass';

  if (move.type === 'move') {
    const to = move.to;
    if (!to || !Number.isInteger(to.col) || !Number.isInteger(to.row)) {
      return false;
    }
    return pawnMovesFor(state, state.current).some(
      (m) => m.col === to.col && m.row === to.row
    );
  }

  if (move.type === 'wall') {
    const at = move.at;
    if (!at || !Number.isInteger(at.col) || !Number.isInteger(at.row)) {
      return false;
    }
    const wallSet = buildWallSet(state.walls);
    return isLegalWallPlacement(state, wallSet, at.col, at.row, move.orientation);
  }

  if (move.type === 'ability') {
    return abilityMovesFor(state).some((c) => sameAbilityMove(c, move));
  }

  if (move.type === 'pass') {
    return nonPassMoves(state).length === 0;
  }

  return false;
}

/**
 * Apply a move, returning a NEW state (input state is never mutated).
 * Throws an Error on illegal moves. Handles win detection (reaching the
 * goal row), ability charge bookkeeping, gimmick effects (swamp), turn
 * switching, and turn/history updates.
 */
export function applyMove(state, move) {
  if (!isLegal(state, move)) {
    throw new Error('Illegal move: ' + JSON.stringify(move));
  }

  const next = clone(state);
  const playerIndex = next.current;
  const player = next.players[playerIndex];

  // The swamp restriction lasts exactly one own turn: it was enforced by
  // the legality check above, so serving any move clears it.
  if (player.effects && player.effects.swamped) {
    delete player.effects.swamped;
  }
  // The hex curse also lasts exactly one own turn: the only legal move while
  // hexed is `pass`, and serving it clears the curse (symmetric to swamped).
  if (player.effects && player.effects.hexed) {
    delete player.effects.hexed;
  }

  // Snapshot every pawn cell BEFORE the move so the landing pipeline can
  // react to ANY pawn relocated this turn — the mover, or an opponent moved
  // by shove/swap. (For all classic/existing move types only the mover's
  // pawn changes, so this is exactly the previous single-pawn behavior.)
  const before = next.players.map((p) => ({ col: p.pawn.col, row: p.pawn.row }));

  if (move.type === 'move') {
    player.pawn = { col: move.to.col, row: move.to.row };
  } else if (move.type === 'wall') {
    next.walls.push({
      col: move.at.col,
      row: move.at.row,
      orientation: move.orientation,
      owner: playerIndex,
    });
    player.wallsLeft -= 1;
  } else if (move.type === 'ability') {
    const def = ABILITIES[move.ability];
    def.apply(next, playerIndex, move, abilityContext(next, playerIndex));
    player.ability.usesLeft -= 1;
  }
  // 'pass': no board change.

  // Generalized landing pipeline: applies to every pawn that ended this turn
  // on a new cell (spec: a pawn that stops on a swamp by ANY means becomes
  // swamped; a shove/swap can relocate the opponent's pawn too).
  for (let i = 0; i < next.players.length; i++) {
    const p = next.players[i];
    if (p.pawn.col === before[i].col && p.pawn.row === before[i].row) continue;
    // Winning is the ACTOR's alone: only the player to move can end the game,
    // by landing their OWN pawn on their OWN goal row. A foe shoved/swapped
    // onto their goal row never wins (you cannot lose on your own turn); the
    // phantom carrying ITSELF onto its goal row is a normal win.
    if (i === playerIndex && p.pawn.row === p.goalRow) {
      next.winner = i;
    }
    // Swamp: stopping on the tile blocks that player's next own turn's pawn
    // movement (explorer's ignoreGimmicks passive makes them immune).
    if (
      isSwampAt(next.config.gimmicks, p.pawn.col, p.pawn.row) &&
      !ignoresGimmicks(next, i)
    ) {
      p.effects = { ...p.effects, swamped: true };
    }
  }

  next.history.push(clone(move));
  next.turn += 1;
  next.current = (playerIndex + 1) % next.players.length;

  return next;
}
